import { Button, ImageUploadField, Input, Label, Popover, PopoverAnchor, PopoverContent } from "@tellescope/ui";
import { Editor } from "@tiptap/react";
import { ImagePlus } from "lucide-react";
import { useTranslation } from "react-i18next";

import { MarkButton } from "./MarkButton";
import { useImageButton } from "./useImageButton";

export interface ImageButtonProps {
  editor: Editor | null;
}

/** Toolbar control that inserts an image, either by pasting a URL or uploading a file. */
export const ImageButton = ({ editor }: ImageButtonProps) => {
  const { t } = useTranslation("templates");
  const { open, url, setUrl, progress, handleOpenChange, applyUrl, insertImage, uploadImage } = useImageButton(editor);

  if (!editor) return null;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <MarkButton label={t("Image")} icon={<ImagePlus />} isActive={false} onClick={() => handleOpenChange(!open)} />
      </PopoverAnchor>
      <PopoverContent align="start" className="flex w-80 flex-col gap-4">
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            // Stop propagation so submitting the image-URL form doesn't bubble up and submit/close
            // the surrounding template form dialog.
            event.preventDefault();
            event.stopPropagation();
            applyUrl();
          }}
        >
          <Label htmlFor="template-image-url">{t("Add from URL")}</Label>
          <Input
            id="template-image-url"
            autoFocus
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder={t("https://example.com/image.png")}
          />
          <Button type="submit" size="sm" className="self-end">
            {t("Insert")}
          </Button>
        </form>
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-xs text-muted-foreground">{t("or")}</span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t("Upload from device")}</Label>
          <ImageUploadField value="" onChange={insertImage} onUpload={uploadImage} progress={progress} fullWidth />
        </div>
      </PopoverContent>
    </Popover>
  );
};
